/* 2026-07 -- Pre-order support
 * - syncCardState now reads policy/quantity/ship (STOQ shipping_text metafield,
 *   stamped by the card snippet) off the selected option / variants JSON and
 *   stores activePreorder + activeShip on the card dataset.
 * - updateCardPreorderFlag() drives the per-card [data-*-preorder-flag] element.
 * - buildStagedEntry carries preorder/ship per staged item; renderSidebar
 *   aggregates them (LATEST ship date wins, same rule as the PDP kit message),
 *   appends a pre-order line under the sidebar items, and switches the CTA
 *   label to "Pre-order N items".
 * - addToCart adds a "Pre-order: Ships from {date}" line-item property,
 *   matching STOQ's PDP wording exactly so all add paths read the same.
 * - Canonical pre-order test everywhere: inventory_policy == 'continue' AND
 *   inventory_quantity <= 0 -- matches zlIsPreorder() on the 2026 PDP.
 */
/**
 * bundle-builder.js
 * Zoomlite Bundle Builder - v1.9
 *
 * CHANGELOG
 * v1.9 - 2026-07: Flat pricing display support (config.flatDisplay).
 *                 - syncCardState, in flat mode, sets the card current price to the
 *                   discounted bundle price and writes the struck original into
 *                   .bb-card-price-original, recomputing both on variant change.
 *                 - The bottom bar, in flat mode once qualified, renders the
 *                   bundled total: discounted price, struck original, percent tag,
 *                   with a --summary class so the caption clamp is dropped. The
 *                   "Bundled total:" label hides on mobile via CSS. Pre-qualify it
 *                   still shows the guidance, and non-flat pages are unchanged.
 *                 - Discount stays display-only; Regios applies the real reduction.
 * v1.8 - 2026-07: Clear the loading class on a successful add.
 *                 - The success path cleared staged state but left
 *                   bb-finalise-btn--loading on the buttons. Harmless before,
 *                   since :disabled masked it, but the CSS press invert makes that
 *                   class visible as a white button plus spinner. The success path
 *                   now removes it, matching the error path, so the reset button
 *                   never sits inverted after the drawer opens.
 * v1.7 - 2026-07: Empty-state prompt now shows on mobile.
 *                 - The mobile bottom bar button kept its own short label and
 *                   never showed the per-group prompt, so on a phone the guidance
 *                   only appeared inside the tap-to-open drawer. renderSidebar now
 *                   routes the guidance (ctaText) into the mobile bar info line
 *                   while the bundle is incomplete, so the shopper sees "Add your
 *                   carry-on to continue" without opening anything. Once it
 *                   qualifies, the info line returns to the price. The compact
 *                   button stays "Add to cart" / "Add N items to cart".
 * v1.6 - 2026-07: Immediate click feedback on finalise.
 *                 - addToCart sets the finalise buttons to "Adding..." on click,
 *                   so the wait for /cart/add.js reads as a deliberate save rather
 *                   than dead air. The drawer still opens on success, so an empty
 *                   cart never flashes on a bundle page.
 *                 - On error, renderSidebar restores the real button labels and
 *                   enabled state instead of leaving "Adding..." stuck.
 * v1.5 - 2026-07: Per-group full-sentence empty-state button prompt.
 *                 - Reads data-group-prompt off each base group (the per-group
 *                   "Empty-state button prompt" field in the schema).
 *                 - New config flag designMode, from data-design-mode.
 *                 - New helpers firstMissingBaseGroup and missingBaseButtonText.
 *                   The summary button, while a base group is empty, shows the
 *                   group's full prompt sentence verbatim. Blank prompt shows a
 *                   flag "[Set prompt: Label]" inside the theme editor so a new
 *                   person fills it, and falls back to "Add your Label to
 *                   continue" on the live storefront.
 *                 - The progress line under the bar keeps its short auto text.
 * Architecture: Staged cart model
 * - Items staged in JS state, never in cart until "Add to Cart" fires
 * - One POST to /cart/add.js with full items[] array
 * - Dispatches cart:open to trigger Unicorn drawer
 * - Each section instance is fully independent
 * - Page-level summary section reads all instances
 *
 * CHANGELOG
 * v1.4 - 2026-07: One shared qualification rule for the button and the discount.
 *                 - Staged entries now carry groupId, read from the card
 *                   data-group-id attribute (set per product group).
 *                 - New helpers: groupFilled, getBaseGroupIds, hasGroups,
 *                   hasAddonGroups, baseGroupsAllFilled, firstMissingBaseLabel,
 *                   qualifies.
 *                 - qualifies(): every Base group must hold at least one item,
 *                   and if any Add-on groups exist, at least one Add-on item is
 *                   staged. Both canAddToCart and qualifiesForDiscount now read
 *                   qualifies() in flat mode, so the button and the discount
 *                   preview unlock together. Two Base groups need one item from
 *                   each. One Base plus Add-on groups needs the base plus one
 *                   add-on from anywhere.
 *                 - Legacy flat pages with no groups keep the old single-base
 *                   rule. Quantity-tiers mode is unchanged.
 *                 - CTA and progress name the missing Base group first, then the
 *                   add-on step. Progress bar spans every base group plus the
 *                   add-on step.
 * v1.3 - 2026-07: Base plus add-on discount model and group roles.
 *                 - Staged entries now carry role (base or add-on), read from the
 *                   card data-group-role attribute (set per product group).
 *                 - New config: discountModel, flatPct, baseNoun (from section data attrs).
 *                 - New helpers: getBaseQty, getAddonQty, hasBase, qualifiesForDiscount,
 *                   getEffectiveDiscountPct, canAddToCart.
 *                 - renderSidebar and the page summary use getEffectiveDiscountPct.
 *                   In base_addon_flat mode the flat percentage applies only when a base
 *                   item plus one add-on are staged, and Add to Cart holds until a base
 *                   item is present. Quantity-tiers mode is unchanged.
 *                 - Cart line items now also carry _bundle_role for a future discount Function.
 * v1.2 - 2026-07: Hide the page mobile bundle bar while a colour modal is open,
 *                 so the modal footer (item count + Confirm) stays visible on mobile.
 *                 Adds this.mobileBar in the ColourPickerModal constructor and toggles
 *                 .bb-mobile-bar--suppressed in open(), close(), and closeAll().
 * v1.1 — Fix: Confirm button no longer disabled when all units removed to zero
 *         Button label changes to "Remove All" when totalQty === 0
 *         Unit count added to trigger button label alongside colour count
 */

(function () {
  'use strict';

  // ─── Page-level registry so the summary section can read all instances ───
  window.BundleBuilderInstances = window.BundleBuilderInstances || [];

  // ─── Utility: format money (cents → "$X.XX") ───────────────────────────
  // Pre-order (2026-07): parse STOQ dates like "28th May 2026" for compare.
  function parseShipDate(str) {
    if (!str) return 0;
    var ts = Date.parse(String(str).replace(/(\d+)(st|nd|rd|th)/, '$1'));
    return isNaN(ts) ? 0 : ts;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }


  // ═══════════════════════════════════════════════════════════════════════
  // BundleBuilder — one instance per section
  // ═══════════════════════════════════════════════════════════════════════
  class BundleBuilder {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;

      // Config from data attributes (written by Liquid)
      this.config = {
        minItems: parseInt(sectionEl.dataset.minItems || 0),
        maxItems: parseInt(sectionEl.dataset.maxItems || 999),
        tiers: JSON.parse(sectionEl.dataset.tiers || '[]'),
        collectionHandle: sectionEl.dataset.collectionHandle || '',
        bundleProperty: sectionEl.dataset.bundleProperty || '_Bundle',
        discountModel: sectionEl.dataset.discountModel || 'quantity_tiers',
        flatPct: parseInt(sectionEl.dataset.flatPct || 0),
        baseNoun: sectionEl.dataset.baseNoun || 'base item',
        designMode: sectionEl.dataset.designMode === 'true',
        flatDisplay: sectionEl.dataset.flatDisplay === 'true',
      };

      // Staged state: { variantId: { id, qty, price, title, variantTitle, image } }
      this.staged = {};

      this.init();

      // Register globally for summary section
      window.BundleBuilderInstances.push(this);
    }

    // ── Initialise all card interactions ──────────────────────────────────
    init() {
      const cards = this.section.querySelectorAll('.bb-card');
      cards.forEach(card => this.initCard(card));
      this.initModals();
      this.initDrawerToggle();
      this.initAddToCart();
      this.renderSidebar();
      // Restore any staged selections saved from previous page visit
      this.restoreFromSession();
    }

    // ── Initialise colour picker modals (modal mode only) ─────────────────
    initModals() {
      this.modals = [];
      this.section.querySelectorAll('.bb-card').forEach(card => {
        if (card.querySelector('.bb-colour-modal')) {
          this.modals.push(new ColourPickerModal(card, this));
        }
      });
    }

    // ── Sync all modal trigger buttons after session restore ──────────────
    syncModalTriggers() {
      if (this.modals) {
        this.modals.forEach(m => m.syncFromStaged());
      }
    }

    // ── Per-card setup ────────────────────────────────────────────────────
    initCard(card) {
      // Variant radio inputs (Impulse pattern: data-index="option1")
      const variantInputs = card.querySelectorAll('.bb-variant-input');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => this.onVariantChange(card));
      });

      // ATC button
      const atcBtn = card.querySelector('.bb-atc-btn');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => this.onAddClick(card));
      }

      // Qty stepper (hidden until first add)
      const minus = card.querySelector('.bb-qty-minus');
      const plus = card.querySelector('.bb-qty-plus');
      if (minus) minus.addEventListener('click', () => this.onQtyChange(card, -1));
      if (plus) plus.addEventListener('click', () => this.onQtyChange(card, 1));

      // Set initial active variant state
      this.syncCardState(card);
    }

    // ── Variant changed → update card's active variant data ───────────────
    // Each variant is staged independently — switching colour does NOT migrate
    // qty. The stepper updates to show the newly selected variant's staged qty.
    onVariantChange(card) {
      this.syncCardState(card);
      // Update button/stepper to reflect this variant's own staged qty
      this.syncCardUI(card);
      this.renderSidebar();
      this.renderPageSummary();
      this.saveToSession();
    }

    // ── Read selected variant from card inputs and update data attrs ───────
    syncCardState(card) {
      // Try the hidden select (Impulse sets value on variant change)
      const variantSelect = card.querySelector('.bb-variant-select');
      let variantId = null;
      let variantTitle = null;
      let variantAvailable = true;
      let variantPrice = null;

      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (selectedOption) {
          variantId = selectedOption.value;
          variantTitle = selectedOption.dataset.title || selectedOption.text;
          variantAvailable = selectedOption.dataset.available !== 'false';
          variantPrice = parseInt(selectedOption.dataset.price || card.dataset.price || 0);
          // Pre-order (2026-07): canonical test, mirrors PDP zlIsPreorder()
          card.dataset.activePreorder = (selectedOption.dataset.policy === 'continue' && parseInt(selectedOption.dataset.quantity || '0', 10) <= 0) ? 'true' : 'false';
          card.dataset.activeShip = selectedOption.dataset.ship || '';
        }
      }

      // Fallback: read checked radio inputs
      if (!variantId) {
        const checkedInputs = card.querySelectorAll('.bb-variant-input:checked');
        if (checkedInputs.length > 0) {
          // Find matching variant from the variants data
          const variantsData = JSON.parse(card.dataset.variants || '[]');
          const selectedValues = Array.from(checkedInputs).map(i => i.value);
          const match = variantsData.find(v =>
            selectedValues.every(val => v.options.includes(val))
          );
          if (match) {
            variantId = String(match.id);
            variantTitle = match.title === 'Default Title' ? '' : match.title;
            variantAvailable = match.available;
            variantPrice = match.price;
            // Pre-order (2026-07): same canonical test from variants JSON
            card.dataset.activePreorder = (match.policy === 'continue' && (match.qty || 0) <= 0) ? 'true' : 'false';
            card.dataset.activeShip = match.ship || '';
          }
        }
      }

      // Store on card element
      if (variantId) {
        card.dataset.activeVariantId = variantId;
        card.dataset.activeVariantTitle = variantTitle || '';
        card.dataset.activeVariantAvailable = variantAvailable;
        card.dataset.activeVariantPrice = variantPrice || card.dataset.price || 0;
      }

      this.updateCardPreorderFlag(card);

      // Update OOS state
      const atcBtn = card.querySelector('.bb-atc-btn');
      if (atcBtn) {
        if (!variantAvailable) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold Out';
          atcBtn.classList.add('bb-atc-btn--soldout');
        } else {
          atcBtn.disabled = false;
          atcBtn.classList.remove('bb-atc-btn--soldout');
          // Don't reset text if it already shows a count
          if (!card.classList.contains('bb-card--added')) {
            atcBtn.textContent = atcBtn.dataset.defaultText || 'Add to Bundle';
          }
        }
      }

      // Update displayed price — target the current price span only
      const priceCurrentEl = card.querySelector('.bb-card-price-current');
      if (priceCurrentEl && variantPrice) {
        if (this.config.flatDisplay && this.config.flatPct > 0) {
          // Flat mode: current shows the discounted bundle price, struck shows the original.
          const discounted = Math.round(variantPrice * (100 - this.config.flatPct) / 100);
          priceCurrentEl.textContent = formatMoney(discounted);
          const struckEl = card.querySelector('.bb-card-price-original');
          if (struckEl) struckEl.textContent = formatMoney(variantPrice);
        } else {
          priceCurrentEl.textContent = formatMoney(variantPrice);
        }
      }

      // Swap image to match variant if available
      if (variantId) {
        this.swapCardImage(card, variantId);
      }
    }

    // ── Swap card image to match selected variant ─────────────────────────
    swapCardImage(card, variantId) {
      const imgEl = card.querySelector('.bb-card-img');
      if (!imgEl) return;
      const variantsData = JSON.parse(card.dataset.variants || '[]');
      const variant = variantsData.find(v => String(v.id) === String(variantId));
      if (variant && variant.featured_image && variant.featured_image.src) {
        const src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_400x$1');
        imgEl.src = src;
      }
    }

    // ── Add to bundle clicked ─────────────────────────────────────────────
    onAddClick(card) {
      const variantId = card.dataset.activeVariantId;
      if (!variantId) return;

      const totalQty = this.getTotalQty();
      if (totalQty >= this.config.maxItems) {
        this.flashMessage(`Maximum ${this.config.maxItems} items allowed in this bundle.`);
        return;
      }

      if (this.staged[variantId]) {
        this.staged[variantId].qty += 1;
      } else {
        this.staged[variantId] = this.buildStagedEntry(card, 1);
      }

      this.updateCardUI(card, this.staged[variantId].qty);
      this.renderSidebar();
      this.renderPageSummary();
      this.saveToSession();
    }

    // ── Qty stepper clicked ───────────────────────────────────────────────
    onQtyChange(card, delta) {
      const variantId = card.dataset.activeVariantId;
      if (!variantId || !this.staged[variantId]) return;

      const totalQty = this.getTotalQty();
      if (delta > 0 && totalQty >= this.config.maxItems) {
        this.flashMessage(`Maximum ${this.config.maxItems} items allowed.`);
        return;
      }

      this.staged[variantId].qty = Math.max(0, this.staged[variantId].qty + delta);

      if (this.staged[variantId].qty === 0) {
        delete this.staged[variantId];
        this.resetCardUI(card);
      } else {
        this.updateCardUI(card, this.staged[variantId].qty);
      }

      this.renderSidebar();
      this.renderPageSummary();
      this.saveToSession();
    }

    // ── Build a staged entry from card data ───────────────────────────────
    // ── Pre-order flag on card (2026-07) ──────────────────────────────
    updateCardPreorderFlag(card) {
      const flag = card.querySelector('[data-bb-preorder-flag]');
      if (!flag) return;
      const ship = card.dataset.activeShip || '';
      if (card.dataset.activePreorder === 'true') {
        flag.textContent = ship ? ('Pre-order — ships ' + ship) : 'Pre-order — shipping date to be confirmed';
        flag.hidden = false;
      } else {
        flag.hidden = true;
      }
    }

    buildStagedEntry(card, qty) {
      return {
        id: card.dataset.activeVariantId,
        qty: qty,
        price: parseInt(card.dataset.activeVariantPrice || 0),
        title: card.dataset.title || '',
        variantTitle: card.dataset.activeVariantTitle || '',
        image: card.querySelector('.bb-card-img')?.src || '',
        role: card.dataset.groupRole || 'addon',
        groupId: card.dataset.groupId || '',
        preorder: card.dataset.activePreorder === 'true',
        ship: card.dataset.activeShip || '',
      };
    }

    // ── Update card UI: button → stepper (qty = this variant's qty only) ──
    updateCardUI(card, qty) {
      card.classList.add('bb-card--added');
      const atcBtn = card.querySelector('.bb-atc-btn');
      const stepper = card.querySelector('.bb-stepper');
      const qtyDisplay = card.querySelector('.bb-qty-display');

      if (atcBtn) atcBtn.style.display = 'none';
      if (stepper) stepper.style.display = 'flex';
      // Show qty for THIS specific variant, not total across all variants
      if (qtyDisplay) qtyDisplay.textContent = qty;
    }

    // ── Reset card UI: stepper → button ──────────────────────────────────
    // Only fully resets if no variants from this card remain staged
    resetCardUI(card) {
      card.classList.remove('bb-card--added');
      const atcBtn = card.querySelector('.bb-atc-btn');
      const stepper = card.querySelector('.bb-stepper');
      if (atcBtn) atcBtn.style.display = '';
      if (stepper) stepper.style.display = 'none';
    }

    // ── Sync card UI to currently selected variant's staged qty ───────────
    // Call this after variant change so stepper reflects the new variant
    syncCardUI(card) {
      const variantId = card.dataset.activeVariantId;
      const stagedEntry = variantId ? this.staged[variantId] : null;
      if (stagedEntry && stagedEntry.qty > 0) {
        this.updateCardUI(card, stagedEntry.qty);
      } else {
        this.resetCardUI(card);
      }
    }

    // ── Session storage persistence ──────────────────────────────────────
    saveToSession() {
      try {
        sessionStorage.setItem(
          'bb-staged-' + this.sectionId,
          JSON.stringify(this.staged)
        );
      } catch(e) {
        // sessionStorage unavailable (private mode etc) — fail silently
      }
    }

    restoreFromSession() {
      try {
        const saved = sessionStorage.getItem('bb-staged-' + this.sectionId);
        if (!saved) return;
        const parsed = JSON.parse(saved);
        if (!parsed || typeof parsed !== 'object') return;

        // Validate each entry still has a valid variant ID before restoring
        // (products could have changed since session was saved)
        this.staged = parsed;

        // Rebuild card UIs to reflect restored state
        this.section.querySelectorAll('.bb-card').forEach(card => {
          this.syncCardUI(card);
        });

        this.renderSidebar();
        this.renderPageSummary();
        // Sync modal trigger button text to reflect restored selections
        this.syncModalTriggers();
      } catch(e) {
        // Corrupt session data — clear and start fresh
        try { sessionStorage.removeItem('bb-staged-' + this.sectionId); } catch(e2) {}
      }
    }

    clearSession() {
      try {
        sessionStorage.removeItem('bb-staged-' + this.sectionId);
      } catch(e) {}
    }

    // ── Helpers ───────────────────────────────────────────────────────────
    getTotalQty() {
      return Object.values(this.staged).reduce((sum, item) => sum + item.qty, 0);
    }

    getTotalPrice() {
      return Object.values(this.staged).reduce((sum, item) => sum + (item.price * item.qty), 0);
    }

    getActiveTier() {
      const qty = this.getTotalQty();
      return this.config.tiers
        .filter(t => qty >= t.minQty)
        .sort((a, b) => b.minQty - a.minQty)[0] || null;
    }

    getNextTier() {
      const qty = this.getTotalQty();
      return this.config.tiers
        .filter(t => qty < t.minQty)
        .sort((a, b) => a.minQty - b.minQty)[0] || null;
    }

    // ── Base plus add-on helpers ──────────────────────────────────────────
    getBaseQty() {
      return Object.values(this.staged)
        .filter(item => item.role === 'base')
        .reduce((sum, item) => sum + item.qty, 0);
    }

    getAddonQty() {
      return Object.values(this.staged)
        .filter(item => item.role !== 'base')
        .reduce((sum, item) => sum + item.qty, 0);
    }

    hasBase() {
      return this.getBaseQty() > 0;
    }

    // -- Group-aware qualification (base_addon_flat) ------------------------
    // One rule feeds BOTH the button gate and the discount preview, so the two
    // never drift apart. Qualifies when every Base group holds at least one
    // item, and, if any Add-on groups exist, at least one Add-on item is staged.
    groupFilled(id) {
      return Object.values(this.staged).some(i => i.groupId === id && i.qty > 0);
    }

    getBaseGroupIds() {
      return Array.from(this.section.querySelectorAll('.bb-group[data-group-role="base"]'))
        .map(g => g.dataset.groupId).filter(Boolean);
    }

    hasGroups() {
      return this.section.querySelector('.bb-group') !== null;
    }

    hasAddonGroups() {
      return this.section.querySelector('.bb-group[data-group-role="addon"]') !== null;
    }

    baseGroupsAllFilled() {
      return this.getBaseGroupIds().every(id => this.groupFilled(id));
    }

    // Label of the first Base group with nothing staged, for the progress line.
    firstMissingBaseLabel() {
      const groups = this.section.querySelectorAll('.bb-group[data-group-role="base"]');
      for (const g of groups) {
        if (!this.groupFilled(g.dataset.groupId)) {
          return g.dataset.groupLabel || this.config.baseNoun;
        }
      }
      return '';
    }

    // First base group element with nothing staged, or null when all are filled.
    firstMissingBaseGroup() {
      const groups = this.section.querySelectorAll('.bb-group[data-group-role="base"]');
      for (const g of groups) {
        if (!this.groupFilled(g.dataset.groupId)) return g;
      }
      return null;
    }

    // Full-sentence empty-state button text for the first unfilled base group.
    // Merchant prompt (data-group-prompt) wins. Blank shows an editor-only flag
    // so a new person spots the gap, and a safe fallback on the live storefront.
    missingBaseButtonText() {
      const g = this.firstMissingBaseGroup();
      if (!g) return '';
      const prompt = (g.dataset.groupPrompt || '').trim();
      if (prompt) return prompt;
      const label = g.dataset.groupLabel || this.config.baseNoun;
      return this.config.designMode
        ? `[Set prompt: ${label}]`
        : `Add your ${label} to continue`;
    }

    qualifies() {
      // Legacy flat page with no groups: keep the old single-base rule.
      if (!this.hasGroups()) {
        return this.hasBase();
      }
      const baseFilled = this.baseGroupsAllFilled();
      const addonOk = !this.hasAddonGroups() || this.getAddonQty() > 0;
      return baseFilled && addonOk && this.getTotalQty() > 0;
    }

    // Discount preview qualifies on the shared rule in flat mode.
    qualifiesForDiscount() {
      if (this.config.discountModel === 'base_addon_flat') {
        return this.qualifies();
      }
      return true;
    }

    // Percentage the preview applies, per discount model.
    getEffectiveDiscountPct() {
      if (this.config.discountModel === 'base_addon_flat') {
        return this.qualifiesForDiscount() ? this.config.flatPct : 0;
      }
      const tier = this.getActiveTier();
      return tier ? tier.percentage : 0;
    }

    // Add to Cart gate reads the SAME rule as the discount in flat mode.
    // Quantity-tiers mode is unchanged.
    canAddToCart() {
      if (this.config.discountModel === 'base_addon_flat') {
        return this.qualifies();
      }
      return this.getTotalQty() >= Math.max(this.config.minItems, 1);
    }

    getStagedItems() {
      return Object.values(this.staged);
    }

    flashMessage(msg) {
      const el = this.section.querySelector('.bb-flash-message');
      if (!el) return;
      el.textContent = msg;
      el.classList.add('bb-flash-message--visible');
      setTimeout(() => el.classList.remove('bb-flash-message--visible'), 3000);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // SIDEBAR RENDER — updates both desktop sidebar AND mobile drawer
    // ═══════════════════════════════════════════════════════════════════════
    renderSidebar() {
      const totalQty = this.getTotalQty();
      const totalPrice = this.getTotalPrice();
      const activeTier = this.getActiveTier();
      const nextTier = this.getNextTier();
      const items = this.getStagedItems();

      // Discount calc (model-aware: tiers ladder or base plus add-on flat)
      const flatMode = this.config.discountModel === 'base_addon_flat';
      const discountPct = this.getEffectiveDiscountPct();
      const discountAmount = Math.round(totalPrice * discountPct / 100);
      const finalPrice = totalPrice - discountAmount;

      // Progress bar logic
      const minItems = this.config.minItems;
      let progressPct;
      if (flatMode) {
        // Progress across every base group, plus one add-on step when add-on groups exist.
        const baseIds = this.getBaseGroupIds();
        const baseMet = baseIds.filter(id => this.groupFilled(id)).length;
        const addonReq = this.hasAddonGroups() ? 1 : 0;
        const addonMet = (addonReq && this.getAddonQty() > 0) ? 1 : 0;
        const totalReq = baseIds.length + addonReq;
        progressPct = totalReq
          ? Math.min(100, ((baseMet + addonMet) / totalReq) * 100)
          : (this.getTotalQty() > 0 ? 100 : 0);
      } else {
        const maxTierQty = this.config.tiers.length
          ? Math.max(...this.config.tiers.map(t => t.minQty))
          : minItems;
        const progressTarget = Math.max(maxTierQty, minItems, 1);
        progressPct = Math.min(100, (totalQty / progressTarget) * 100);
      }

      let progressMsg = '';
      if (flatMode) {
        const missingBase = this.firstMissingBaseLabel();
        if (missingBase) {
          progressMsg = `Add your ${missingBase} to start your bundle`;
        } else if (this.hasAddonGroups() && this.getAddonQty() === 0) {
          progressMsg = `Add one more item to get ${this.config.flatPct}% off`;
        } else {
          progressMsg = `${this.config.flatPct}% off unlocked`;
        }
      } else if (nextTier) {
        const needed = nextTier.minQty - totalQty;
        progressMsg = needed === 1
          ? `Add 1 more item to unlock ${nextTier.percentage}% off`
          : `Add ${needed} more items to unlock ${nextTier.percentage}% off`;
      } else if (activeTier) {
        progressMsg = `${activeTier.percentage}% discount unlocked! 🎉`;
      } else if (minItems > 0 && totalQty < minItems) {
        progressMsg = `Add ${minItems - totalQty} more item${minItems - totalQty !== 1 ? 's' : ''} to complete your bundle`;
      } else {
        progressMsg = totalQty > 0 ? 'Ready to add to cart!' : 'Start adding items below';
      }

      const itemsHtml = items.length === 0
        ? '<p class="bb-sidebar-empty">No items added yet.<br>Select products below.</p>'
        : items.map(item => `
            <div class="bb-sidebar-item">
              <div class="bb-sidebar-item-info">
                <span class="bb-sidebar-item-title">${item.title}${item.variantTitle ? ' — ' + item.variantTitle : ''}</span>
                <span class="bb-sidebar-item-qty">× ${item.qty}</span>
              </div>
              <span class="bb-sidebar-item-price">${formatMoney(item.price * item.qty)}</span>
            </div>
          `).join('');

      const canAdd = this.canAddToCart();
      let ctaText;
      if (canAdd) {
        ctaText = `Add ${totalQty} item${totalQty !== 1 ? 's' : ''} to Cart`;
      } else if (flatMode) {
        const basePrompt = this.missingBaseButtonText();
        if (basePrompt) {
          ctaText = basePrompt;
        } else if (this.hasAddonGroups() && this.getAddonQty() === 0) {
          ctaText = 'Add one more item to continue';
        } else {
          ctaText = 'Add to Cart';
        }
      } else if (!flatMode && this.config.minItems > 1) {
        ctaText = `Add ${this.config.minItems - totalQty} more to continue`;
      } else {
        ctaText = 'Add to Cart';
      }

      // Update ALL panels: desktop sidebar + mobile drawer
      // Pre-order aggregate (2026-07): any staged pre-order item flips the
      // CTA; message shows the LATEST ship date (bundle ships when all can).
      const preItems = items.filter(i => i.preorder && i.qty > 0);
      const hasPreorder = preItems.length > 0;
      let preorderShip = null, _preTs = 0;
      preItems.forEach(i => { const ts = parseShipDate(i.ship); if (ts > _preTs) { _preTs = ts; preorderShip = i.ship; } });
      if (canAdd && hasPreorder) {
        ctaText = 'Pre-order ' + totalQty + ' item' + (totalQty !== 1 ? 's' : '');
      }
      const preorderMsgHtml = hasPreorder
        ? '<div class="bb-preorder-msg">' + (preorderShip ? 'Pre-order — estimated shipping ' + preorderShip : 'Pre-order — shipping date to be confirmed') + '</div>'
        : '';

      const panels = [
        this.section.querySelector('.bb-sidebar'),
        this.section.querySelector('.bb-mobile-drawer')
      ].filter(Boolean);

      panels.forEach(panel => {
        const progressFill = panel.querySelector('.bb-progress-fill');
        const progressText = panel.querySelector('.bb-progress-text');
        const itemsList = panel.querySelector('.bb-sidebar-items');
        const subtotalEl = panel.querySelector('.bb-subtotal-value');
        const discountRow = panel.querySelector('.bb-discount-row');
        const discountValueEl = panel.querySelector('.bb-discount-value');
        const finalEl = panel.querySelector('.bb-final-value');
        const ctaBtn = panel.querySelector('.bb-finalise-btn');

        if (progressFill) progressFill.style.width = progressPct + '%';
        if (progressText) progressText.textContent = progressMsg;
        if (itemsList) itemsList.innerHTML = itemsHtml + preorderMsgHtml;
        if (subtotalEl) subtotalEl.textContent = formatMoney(totalPrice);
        if (discountRow) discountRow.style.display = discountAmount > 0 ? 'flex' : 'none';
        if (discountValueEl) discountValueEl.textContent = '−' + formatMoney(discountAmount);
        if (finalEl) finalEl.textContent = formatMoney(finalPrice);

        // Tier row active state
        panel.querySelectorAll('.bb-tier-row').forEach(row => {
          const rowPct = parseInt(row.dataset.tierPct || 0);
          row.classList.toggle('bb-tier-row--active', activeTier && rowPct === activeTier.percentage);
        });

        if (ctaBtn) {
          ctaBtn.disabled = !canAdd;
          ctaBtn.classList.toggle('bb-finalise-btn--ready', canAdd);
          ctaBtn.textContent = ctaText;
        }
      });

      // ── Mobile bar ──
      const countBadge = this.section.querySelector('.bb-mobile-count');
      if (countBadge) {
        countBadge.textContent = totalQty;
        countBadge.style.display = totalQty > 0 ? 'inline-flex' : 'none';
      }

      // Mobile bar guidance line. While the bundle does not yet qualify, show the
      // same empty-state prompt the sidebar and drawer buttons show, since the
      // compact mobile button has no room for a full sentence. Once it qualifies,
      // show the price.
      const mobileTotal = this.section.querySelector('.bb-mobile-total');
      if (mobileTotal) {
        if (this.config.flatDisplay && canAdd) {
          // Bundled total: discounted price, struck original, and a percent tag.
          // The "Bundled total:" label is hidden on mobile via CSS.
          const pct = this.config.flatPct;
          mobileTotal.classList.add('bb-mobile-total--summary');
          mobileTotal.innerHTML =
            '<span class="bb-bar-label">Bundled total: </span>' +
            '<span class="bb-bar-final">' + formatMoney(finalPrice) + '</span> ' +
            '<s class="bb-bar-original">' + formatMoney(totalPrice) + '</s>' +
            (pct > 0 ? ' <span class="bb-bar-tag">' + pct + '% OFF</span>' : '');
        } else {
          mobileTotal.classList.remove('bb-mobile-total--summary');
          mobileTotal.textContent = canAdd ? formatMoney(finalPrice) : ctaText;
        }
      }

      const mobileBar = this.section.querySelector('.bb-mobile-bar');
      if (mobileBar) mobileBar.classList.toggle('bb-mobile-bar--visible', totalQty > 0);

      const mobileFinaliseBtn = this.section.querySelector('.bb-finalise-btn--mobile');
      if (mobileFinaliseBtn) {
        mobileFinaliseBtn.disabled = !canAdd;
        mobileFinaliseBtn.classList.toggle('bb-finalise-btn--ready', canAdd);
        mobileFinaliseBtn.textContent = canAdd
          ? (hasPreorder ? `Pre-order ${totalQty} item${totalQty !== 1 ? 's' : ''}` : `Add ${totalQty} item${totalQty !== 1 ? 's' : ''} to cart`)
          : 'Add to cart';
      }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PAGE SUMMARY — notify the summary section to re-render
    // ═══════════════════════════════════════════════════════════════════════
    renderPageSummary() {
      document.dispatchEvent(new CustomEvent('bundlebuilder:updated', {
        detail: { sectionId: this.sectionId }
      }));
    }

    // ═══════════════════════════════════════════════════════════════════════
    // MOBILE DRAWER TOGGLE
    // ═══════════════════════════════════════════════════════════════════════
    initDrawerToggle() {
      const mobileBar = this.section.querySelector('.bb-mobile-bar');
      const mobileDrawer = this.section.querySelector('.bb-mobile-drawer');
      const drawerClose = this.section.querySelector('.bb-drawer-close');
      const drawerOverlay = this.section.querySelector('.bb-drawer-overlay');

      const openDrawer = () => {
        if (mobileDrawer) mobileDrawer.classList.add('bb-mobile-drawer--open');
        if (drawerOverlay) drawerOverlay.style.display = 'block';
        document.body.style.overflow = 'hidden';
      };

      const closeDrawer = () => {
        if (mobileDrawer) mobileDrawer.classList.remove('bb-mobile-drawer--open');
        if (drawerOverlay) drawerOverlay.style.display = 'none';
        document.body.style.overflow = '';
      };

      // Tapping the info area of the bar opens the drawer
      // Tapping the ATC button directly triggers cart add
      if (mobileBar) {
        mobileBar.addEventListener('click', (e) => {
          if (!e.target.closest('.bb-finalise-btn--mobile')) {
            openDrawer();
          }
        });
      }

      if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
      if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // ADD TO CART
    // ═══════════════════════════════════════════════════════════════════════
    initAddToCart() {
      // Both desktop sidebar and mobile drawer CTA buttons
      this.section.querySelectorAll('.bb-finalise-btn').forEach(btn => {
        btn.addEventListener('click', () => this.addToCart());
      });
    }

    async addToCart() {
      const items = this.getStagedItems();
      if (items.length === 0) return;

      const ctaBtns = this.section.querySelectorAll('.bb-finalise-btn');
      ctaBtns.forEach(btn => {
        btn.disabled = true;
        btn.classList.add('bb-finalise-btn--loading');
        btn.textContent = 'Adding...';
      });

      // Build items array — include _Bundle property for tracking
      const cartItems = items.map(item => {
        const props = {
          [this.config.bundleProperty]: this.config.collectionHandle || 'bundle',
          _bundle_role: item.role || 'addon',
        };
        // Pre-order (2026-07): property key + wording match STOQ's PDP flow
        // exactly ("Pre-order: Ships from {date}") so all add paths read the
        // same in cart, checkout and orders.
        if (item.preorder) props['Pre-order'] = item.ship ? ('Ships from ' + item.ship) : 'Yes';
        return { id: parseInt(item.id), quantity: item.qty, properties: props };
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });

        const data = await response.json();

        if (data.status && data.status >= 400) {
          throw new Error(data.description || 'Could not add to cart');
        }

        // Success — clear staged state, reset UI and session
        this.clearSession();
        this.clearBundle();

        // Clear the press/invert state so the reset button is not left inverted.
        ctaBtns.forEach(btn => btn.classList.remove('bb-finalise-btn--loading'));

        // Trigger Unicorn cart drawer
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('cart:build'));

      } catch (err) {
        console.error('[BundleBuilder] Cart add error:', err);
        this.flashMessage('Something went wrong. Please try again.');
        ctaBtns.forEach(btn => btn.classList.remove('bb-finalise-btn--loading'));
        // Restore button labels and enabled state from current staged data.
        this.renderSidebar();
      }
    }

    // ── Clear all staged items and reset card UIs ─────────────────────────
    clearBundle() {
      this.staged = {};
      this.section.querySelectorAll('.bb-card').forEach(card => this.syncCardUI(card));
      this.renderSidebar();
      this.renderPageSummary();

      // Close mobile drawer
      const mobileDrawer = this.section.querySelector('.bb-mobile-drawer');
      if (mobileDrawer) mobileDrawer.classList.remove('bb-mobile-drawer--open');
      document.body.style.overflow = '';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ColourPickerModal — modal variant selector for a single card
  // ═══════════════════════════════════════════════════════════════════════
  class ColourPickerModal {
    constructor(card, bundleInstance) {
      this.card = card;
      this.bundle = bundleInstance;
      this.productId = card.dataset.productId;
      this.modal = card.querySelector('.bb-colour-modal');
      this.backdrop = card.closest('.bb-section').querySelector('.bb-modal-backdrop');
      this.mobileBar = card.closest('.bb-section').querySelector('.bb-mobile-bar');
      this.triggerBtn = card.querySelector('.bb-modal-trigger');

      if (!this.modal) return;

      this.init();
    }

    init() {
      // Open on trigger button
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener('click', () => this.open());
      }

      // Close button inside modal
      const closeBtn = this.modal.querySelector('.bb-colour-modal-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      // Backdrop click closes
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.closeAll());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) this.close();
      });

      // Row steppers
      this.modal.querySelectorAll('.bb-modal-row').forEach(row => {
        const minus = row.querySelector('.bb-modal-minus');
        const plus = row.querySelector('.bb-modal-plus');
        if (minus) minus.addEventListener('click', () => this.changeRowQty(row, -1));
        if (plus) plus.addEventListener('click', () => this.changeRowQty(row, 1));
      });

      // Confirm button
      const confirmBtn = this.modal.querySelector('.bb-modal-confirm');
      if (confirmBtn) confirmBtn.addEventListener('click', () => this.confirm());
    }

    open() {
      // Sync modal row qtys from current staged state
      this.modal.querySelectorAll('.bb-modal-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const staged = this.bundle.staged[variantId];
        const qtyEl = row.querySelector('.bb-modal-qty');
        if (qtyEl) qtyEl.textContent = staged ? staged.qty : 0;
        row.classList.toggle('bb-modal-row--selected', !!(staged && staged.qty > 0));
      });

      this.updateModalSummary();
      this.modal.hidden = false;
      if (this.backdrop) this.backdrop.classList.add('bb-modal-backdrop--visible');
      if (this.mobileBar) this.mobileBar.classList.add('bb-mobile-bar--suppressed');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.modal.hidden = true;
      if (this.backdrop) this.backdrop.classList.remove('bb-modal-backdrop--visible');
      if (this.mobileBar) this.mobileBar.classList.remove('bb-mobile-bar--suppressed');
      document.body.style.overflow = '';
    }

    closeAll() {
      // Close all open modals in this section
      this.card.closest('.bb-section').querySelectorAll('.bb-colour-modal').forEach(m => {
        m.hidden = true;
      });
      if (this.backdrop) this.backdrop.classList.remove('bb-modal-backdrop--visible');
      if (this.mobileBar) this.mobileBar.classList.remove('bb-mobile-bar--suppressed');
      document.body.style.overflow = '';
    }

    changeRowQty(row, delta) {
      const qtyEl = row.querySelector('.bb-modal-qty');
      if (!qtyEl) return;
      const current = parseInt(qtyEl.textContent) || 0;
      const newQty = Math.max(0, current + delta);
      qtyEl.textContent = newQty;
      row.classList.toggle('bb-modal-row--selected', newQty > 0);
      this.updateModalSummary();
    }

    // ── FIX v1.1: Confirm button always enabled so user can remove all items ──
    updateModalSummary() {
      let totalQty = 0;
      let totalPrice = 0;

      this.modal.querySelectorAll('.bb-modal-row').forEach(row => {
        const qty = parseInt(row.querySelector('.bb-modal-qty')?.textContent) || 0;
        const price = parseInt(row.dataset.variantPrice) || 0;
        totalQty += qty;
        totalPrice += qty * price;
      });

      const countEl = this.modal.querySelector('.bb-modal-summary-count');
      const priceEl = this.modal.querySelector('.bb-modal-summary-price');
      const confirmBtn = this.modal.querySelector('.bb-modal-confirm');

      if (countEl) {
        countEl.textContent = totalQty === 0
          ? 'No colours selected'
          : `${totalQty} item${totalQty !== 1 ? 's' : ''} selected`;
      }
      if (priceEl) priceEl.textContent = formatMoney(totalPrice);

      // Always enabled — zero is a valid state (remove all)
      // Label changes to "Remove All" when everything is zeroed out
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.textContent = totalQty === 0 ? 'Remove All' : 'Confirm Selection';
        confirmBtn.classList.toggle('bb-modal-confirm--ready', totalQty > 0);
      }
    }

    confirm() {
      // Commit modal selections to bundle staged state
      this.modal.querySelectorAll('.bb-modal-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const qty = parseInt(row.querySelector('.bb-modal-qty')?.textContent) || 0;

        if (qty > 0) {
          this.bundle.staged[variantId] = {
            id: variantId,
            qty: qty,
            price: parseInt(row.dataset.variantPrice) || 0,
            title: this.card.dataset.title || '',
            variantTitle: row.dataset.variantTitle || '',
            image: this.card.querySelector('.bb-card-img')?.src || '',
            role: this.card.dataset.groupRole || 'addon',
            groupId: this.card.dataset.groupId || '',
            preorder: row.dataset.preorder === 'true',
            ship: row.dataset.ship || '',
          };
        } else {
          // Remove if qty reduced to 0
          delete this.bundle.staged[variantId];
        }
      });

      // Update trigger button text to reflect selection
      this.updateTriggerButton();

      this.bundle.renderSidebar();
      this.bundle.renderPageSummary();
      this.bundle.saveToSession();
      this.close();
    }

    updateTriggerButton() {
      if (!this.triggerBtn) return;

      // Count staged variants belonging to this product
      const variants = this.card.dataset.variants ? JSON.parse(this.card.dataset.variants) : [];
      const variantIds = variants.map(v => String(v.id));
      const stagedForCard = variantIds
        .filter(id => this.bundle.staged[id] && this.bundle.staged[id].qty > 0)
        .map(id => this.bundle.staged[id]);

      if (stagedForCard.length === 0) {
        this.triggerBtn.textContent = 'Choose Colour →';
        this.triggerBtn.classList.remove('bb-modal-trigger--selected');
        this.card.classList.remove('bb-card--added');
      } else {
        const totalQty = stagedForCard.reduce((s, v) => s + v.qty, 0);
        const colourCount = stagedForCard.length;
        const label = colourCount === 1
          ? `✓ ${stagedForCard[0].variantTitle} · ${totalQty} unit${totalQty !== 1 ? 's' : ''} · Change`
          : `✓ ${colourCount} colours · ${totalQty} unit${totalQty !== 1 ? 's' : ''} · Change`;
        this.triggerBtn.textContent = label;
        this.triggerBtn.classList.add('bb-modal-trigger--selected');
        this.card.classList.add('bb-card--added');
      }
    }

    // Called on session restore to sync trigger button state
    syncFromStaged() {
      // Sync modal row quantities from staged state (for session restore)
      this.modal.querySelectorAll('.bb-modal-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const staged = this.bundle.staged[variantId];
        const qtyEl = row.querySelector('.bb-modal-qty');
        if (qtyEl) qtyEl.textContent = staged ? staged.qty : 0;
        row.classList.toggle('bb-modal-row--selected', !!(staged && staged.qty > 0));
      });
      this.updateTriggerButton();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // BundlePageSummary — the summary section at the bottom of the page
  // ═══════════════════════════════════════════════════════════════════════
  class BundlePageSummary {
    constructor(sectionEl) {
      this.section = sectionEl;
      document.addEventListener('bundlebuilder:updated', () => this.render());
      this.render();
    }

    render() {
      const instances = window.BundleBuilderInstances || [];
      const summaryList = this.section.querySelector('.bb-summary-list');
      if (!summaryList) return;

      let grandTotal = 0;
      let grandDiscount = 0;
      let hasItems = false;
      let html = '';

      instances.forEach(instance => {
        const items = instance.getStagedItems();
        const sectionTitle = instance.section.dataset.sectionTitle || 'Items';
        const discountPct = instance.getEffectiveDiscountPct();
        const subtotal = instance.getTotalPrice();
        const discountAmt = Math.round(subtotal * discountPct / 100);
        const sectionFinal = subtotal - discountAmt;

        grandTotal += subtotal;
        grandDiscount += discountAmt;

        if (items.length === 0) return;
        hasItems = true;

        html += `
          <div class="bb-summary-section">
            <h4 class="bb-summary-section-title">${sectionTitle}</h4>
            ${items.map(item => `
              <div class="bb-summary-item">
                <span class="bb-summary-item-name">${item.title}${item.variantTitle ? ' — ' + item.variantTitle : ''} × ${item.qty}</span>
                <span class="bb-summary-item-price">${formatMoney(item.price * item.qty)}</span>
              </div>
            `).join('')}
            ${discountPct > 0 ? `<div class="bb-summary-discount-note">${discountPct}% bundle discount applied</div>` : ''}
            <div class="bb-summary-section-subtotal">
              <span>Section total</span>
              <span>${formatMoney(sectionFinal)}</span>
            </div>
          </div>
        `;
      });

      if (!hasItems) {
        summaryList.innerHTML = '<p class="bb-summary-empty">Add items to your bundles above to see your summary here.</p>';
      } else {
        summaryList.innerHTML = html;
      }

      // Grand total
      const grandFinal = grandTotal - grandDiscount;
      const grandTotalEl = this.section.querySelector('.bb-grand-total');
      const grandDiscountEl = this.section.querySelector('.bb-grand-discount');
      const grandDiscountRow = this.section.querySelector('.bb-grand-discount-row');
      const grandFinalEl = this.section.querySelector('.bb-grand-final');

      if (grandTotalEl) grandTotalEl.textContent = formatMoney(grandTotal);
      if (grandDiscountEl) grandDiscountEl.textContent = '−' + formatMoney(grandDiscount);
      if (grandDiscountRow) grandDiscountRow.style.display = grandDiscount > 0 ? 'flex' : 'none';
      if (grandFinalEl) grandFinalEl.textContent = formatMoney(grandFinal);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // BOOT — initialise all bundle sections on page
  // ═══════════════════════════════════════════════════════════════════════
  function boot() {
    document.querySelectorAll('.bb-section[data-section-id]').forEach(el => {
      if (!el.dataset.bbInitialised) {
        el.dataset.bbInitialised = 'true';
        new BundleBuilder(el);
      }
    });

    document.querySelectorAll('.bb-summary-section-wrapper').forEach(el => {
      if (!el.dataset.bbInitialised) {
        el.dataset.bbInitialised = 'true';
        new BundlePageSummary(el);
      }
    });
  }

  // ── Reliable multi-strategy boot ──────────────────────────────────────
  // Shopify pages with multiple sections can have timing issues where
  // DOMContentLoaded fires before all section HTML is rendered.
  // We boot on DOMContentLoaded, then again on load, then observe for
  // any late-rendering sections via MutationObserver.

  function safeBoot() {
    boot();
    // Re-run after a short delay to catch any sections rendered late
    setTimeout(boot, 300);
    setTimeout(boot, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeBoot);
  } else {
    safeBoot();
  }

  // Shopify theme editor live reload
  document.addEventListener('shopify:section:load', boot);

  // MutationObserver — catch any sections added to DOM after initial load
  const observer = new MutationObserver(function(mutations) {
    let shouldBoot = false;
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('bb-section')) shouldBoot = true;
          if (node.querySelector && node.querySelector('.bb-section')) shouldBoot = true;
        }
      });
    });
    if (shouldBoot) boot();
  });

  observer.observe(document.body, { childList: true, subtree: true });

})();